@file:Suppress("MagicNumber")

package top.wcpe.allincore.ui.runtime.event

import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.Modal
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.overlayOrchestrator
import top.wcpe.allincore.ui.runtime.render.transform.Transform2D
import kotlin.jvm.JvmStatic

/**
 * 内建复合控件的输入行为扩展。
 */
internal object ControlInteractions {

    /** 箭头按钮区域宽度，与 SliderRenderer 保持一致。 */
    private const val ARROW_WIDTH = 8f

    @JvmStatic
    fun collapseDropdownsOnOutsideClick(root: UIElement<*>, layout: LayoutResult, mx: Double, my: Double) {
        // 先处理 modal 子树内的展开 dropdown：modal 用 lastRenderedOrigin 渲染（屏幕坐标），
        // 其内 dropdown 的展开盒必须在屏幕坐标系下与「加回 rootOffset 的鼠标坐标」比较，否则会按
        // modal 原始布局坐标误判点击在面板外而错误收起（与 hitDropdownOverlay / hitModalSubtree 同口径）。
        val modals = overlayOrchestrator().collectVisibleModals(root)
        if (modals.isNotEmpty()) {
            val screenMx = mx + HitTester.rootOffsetX()
            val screenMy = my + HitTester.rootOffsetY()
            for (modal in modals) {
                val rendered = modal.lastRenderedOrigin()
                val modalBox = layout.boxOf(modal)
                val originX = rendered?.get(0) ?: (modalBox?.x() ?: 0f)
                val originY = rendered?.get(1) ?: (modalBox?.y() ?: 0f)
                for (child in modal.children().toList()) {
                    collapseDropdownsOnOutsideClick(
                        child,
                        layout,
                        originX,
                        originY,
                        Transform2D.IDENTITY,
                        screenMx,
                        screenMy,
                    )
                }
            }
        }
        // 主 root 树（root 局部坐标）；递归内部跳过 Modal 子树，避免按原始布局坐标重复处理。
        collapseDropdownsOnOutsideClick(root, layout, 0f, 0f, Transform2D.IDENTITY, mx, my)
    }

    private fun collapseDropdownsOnOutsideClick(
        node: UIElement<*>,
        layout: LayoutResult,
        parentX: Float,
        parentY: Float,
        parentTransform: Transform2D,
        mx: Double,
        my: Double,
    ) {
        // Modal 子树已在入口按屏幕坐标单独处理，主树遍历到 Modal 时跳过，避免按原始布局坐标二次误判。
        if (node is Modal) return
        val box = layout.boxOf(node) ?: return
        val absX = parentX + box.x()
        val absY = parentY + box.y()
        val transform = parentTransform.push(node)
        if (node is Dropdown && node.expanded()) {
            val expandedHeight = box.height() + node.options().size * node.effectiveOptionHeight()
            val expandedBox = transform.transformedBox(absX, absY, box.width(), expandedHeight)
            val inside = !(
                mx < expandedBox[0] ||
                    my < expandedBox[1] ||
                    mx >= expandedBox[0] + expandedBox[2] ||
                    my >= expandedBox[1] + expandedBox[3]
                )
            if (!inside) node.expanded(false)
        }
        val childOriginX = HitGeometry.childOriginX(node, absX)
        val childOriginY = HitGeometry.childOriginY(node, absY)
        for (child in node.children().toList()) {
            collapseDropdownsOnOutsideClick(child, layout, childOriginX, childOriginY, transform, mx, my)
        }
    }

    /**
     * Slider 命中特化：若命中路径末端为 Slider，按鼠标 X 在其轨道内的比例写入 value。
     */
    @Suppress("ReturnCount")
    @JvmStatic
    fun applySliderInteraction(path: List<UIElement<*>>, absBoxes: List<FloatArray>, mx: Double) {
        if (path.isEmpty() || absBoxes.isEmpty()) return
        val tail = path[path.size - 1]
        if (tail !is SliderInputTarget) return
        val absBox = absBoxes[absBoxes.size - 1]
        val absX = absBox[0]
        val absW = absBox[2]
        if (absW <= 0f) return
        val step = tail.step()
        // 左箭头区域：按 step 减小
        if (mx < absX + ARROW_WIDTH) {
            if (step > 0.0) {
                var newVal = tail.value() - step
                if (newVal < tail.min()) newVal = tail.min()
                tail.applySliderValue(newVal)
            }
            return
        }
        // 右箭头区域：按 step 增大
        if (mx >= absX + absW - ARROW_WIDTH) {
            if (step > 0.0) {
                var newVal = tail.value() + step
                if (newVal > tail.max()) newVal = tail.max()
                tail.applySliderValue(newVal)
            }
            return
        }
        // 轨道区域：按比例设置值
        val trackX = absX + ARROW_WIDTH
        val trackW = absW - 2 * ARROW_WIDTH
        if (trackW <= 0.0) return
        val ratio = (mx - trackX) / trackW
        if (!ratio.isFinite()) return
        tail.applySliderRatio(ratio)
    }

    /**
     * TextField 点击特化：若命中路径末端为启用的 TextField，按鼠标局部 X 定位光标列。
     */
    @JvmStatic
    fun applyTextFieldClick(path: List<UIElement<*>>, absBoxes: List<FloatArray>, mx: Double, my: Double) {
        if (path.isEmpty() || absBoxes.isEmpty()) return
        val tail = path[path.size - 1]
        if (tail !is TextFieldInputTarget || !tail.enabled()) return
        val absBox = absBoxes[absBoxes.size - 1]
        tail.setCaretFromLocal(mx - absBox[0], my - absBox[1], false)
    }

    /**
     * Dropdown / TabView 点击特化。
     * @return 是否被本特化分支消费
     */
    @Suppress("CyclomaticComplexMethod")
    @JvmStatic
    fun applySpecialClick(path: List<UIElement<*>>, absBoxes: List<FloatArray>, mx: Double, my: Double): Boolean {
        if (path.isEmpty()) return false
        val tail = path[path.size - 1]
        val absBox = absBoxes[absBoxes.size - 1]
        val ax = absBox[0]
        val ay = absBox[1]
        val aw = absBox[2]
        val ah = absBox[3]
        if (tail is Dropdown && tail.expanded()) {
            val panelTop = ay + ah
            val optionHeight = tail.effectiveOptionHeight() * HitGeometry.scaleY(absBox)
            if (my >= panelTop && tail.options().size > 0 && optionHeight > 0f) {
                val idx = ((my - panelTop) / optionHeight).toInt()
                val inBounds = idx >= 0 && idx < tail.options().size
                if (inBounds && mx >= ax && mx < ax + aw) {
                    tail.selectOption(tail.options()[idx])
                    return true
                }
            }
        }
        if (tail is TabView) {
            val headerHeight = tail.effectiveHeaderHeight() * HitGeometry.scaleY(absBox)
            if (my >= ay && my < ay + headerHeight && tail.tabs().isNotEmpty()) {
                val w = aw / tail.tabs().size
                val idx = ((mx - ax) / w).toInt()
                if (idx >= 0 && idx < tail.tabs().size) {
                    tail.selectTab(idx)
                    return true
                }
            }
        }
        return false
    }
}
